import Rollbar from "rollbar";
import Version from "@/utils/getAppVersion.util";

// Load environment variables
const modeEnv = import.meta.env.APP_NODE_ENV;
const accessToken = import.meta.env.APP_ROLLBAR_TOKEN;

// List of specific error messages to ignore
const ignoreList = [
  "Uncaught TypeError: Cannot redefine property: googletag",
  "TypeError: Cannot redefine property: adoptedStyleSheets",
  "CanceledError: canceled",
  "canceled",
  "Timeout",
  "Network Error",
  "Failed to load Smartech script",
];

/**
 * Rollbar configuration object
 */
const rollbarConfig = {
  accessToken: accessToken,
  captureUncaught: true, // Automatically capture uncaught exceptions
  captureUnhandledRejections: true, // Automatically capture unhandled Promise rejections
  ignoredMessages: ignoreList, // Ignore these exact error messages

  payload: {
    environment: modeEnv, // e.g., development, staging, production
    client: {
      javascript: {
        code_version: Version, // App version, helpful for tracking releases
        source_map_enabled: true, // Enable source maps for better stack traces
      },
    },
  },

  /**
   * Function to programmatically decide whether to ignore an error.
   *
   * @param {boolean} isUncaught - Whether the error was uncaught.
   * @param {Array} args - Arguments passed with the error.
   * @param {Object} payload - The Rollbar payload object.
   * @returns {boolean} - Return true to ignore the error; false to report it.
   */
  checkIgnore: (_, __, payload) => {
    try {
      // Check if the error stack trace includes frames from browser extensions
      const trace = payload?.body?.trace;
      const frames = trace?.frames || [];
      const extensionFrame = frames.find(
        (frame) =>
          frame.filename && frame.filename.startsWith("chrome-extension://"),
      );

      if (extensionFrame) {
        // Ignore the error if any frame comes from a Chrome extension
        // console.debug("Ignoring error from extension frame:", extensionFrame);
        return true;
      }

      // Check if the error message itself mentions a browser extension scheme
      const message = payload?.body?.message?.body || "";
      const extensionInMessage =
        message.includes("chrome-extension://") ||
        message.includes("moz-extension://") || // Firefox extensions
        message.includes("safari-extension://"); // Safari extensions

      if (extensionInMessage) {
        // Ignore the error if the message refers to an extension
        // console.debug("Ignoring error from extension message:", message);
        return true;
      }

      // Ignore `[anonymous]` or missing filenames (common in 3rd party scripts)
      const anonymousFrame = frames?.find(
        (frame) =>
          !frame.filename ||
          frame.filename === "[anonymous]" ||
          frame.filename === "<anonymous>",
      );
      if (anonymousFrame) return true;

      // Ignore known noisy messages like ResizeObserver
      const noisyPatterns = [
        "ResizeObserver loop completed",
        "ResizeObserver loop limit exceeded",
        "ResizeObserver loop completed with undelivered notifications.",
        "Script error",
      ];
      if (message && noisyPatterns.some((pattern) => message.includes(pattern)))
        return true;

      return false; // Allow Rollbar to report all other errors
    } catch (e) {
      // Defensive coding: if checkIgnore logic fails, do not block reporting
      return false;
    }
  },
};

// Create Rollbar instance with the configuration
const rollbar = new Rollbar(rollbarConfig);

export default rollbar;
